import React, { useState, useEffect } from 'react';
import { employeesApi } from '../services/api';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
  role: string;
  status: string;
  created_at: string;
  permissions?: string[];
}

// Available permissions with labels
const AVAILABLE_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'orders', label: 'Orders' },
  { key: 'packing', label: 'Packing Station' },
  { key: 'shipping', label: 'Shipping Station' },
  { key: 'shipments', label: 'Shipments' },
  { key: 'products', label: 'Products' },
  { key: 'employees', label: 'Employees' },
  { key: 'automation', label: 'Automation' },
  { key: 'customers', label: 'Customers' },
  { key: 'blocked-addresses', label: 'Blocked Addresses' },
  { key: 'reports', label: 'Reports' },
  { key: 'time-report', label: 'Time Report' },
  { key: 'settings', label: 'Settings' },
];

// Default permissions for new employees
const DEFAULT_PERMISSIONS = ['dashboard', 'orders', 'packing', 'shipping', 'shipments'];

export const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pin, setPin] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [role, setRole] = useState('employee');
  const [permissions, setPermissions] = useState<string[]>(DEFAULT_PERMISSIONS);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const data = await employeesApi.list();
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      alert('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!firstName || !lastName) {
      alert('First name and last name are required');
      return;
    }

    // PIN is required for new employees, optional for updates
    if (!editingEmployee && !pin) {
      alert('PIN is required for new employees');
      return;
    }

    // If PIN is provided, validate format
    if (pin && !/^\d{4,6}$/.test(pin)) {
      alert('PIN must be 4-6 digits');
      return;
    }

    setLoading(true);
    try {
      if (editingEmployee) {
        // Update existing employee
        await employeesApi.update(editingEmployee.id, {
          first_name: firstName,
          last_name: lastName,
          employee_number: employeeNumber || undefined,
          role,
          pin: pin || undefined, // Only send if changed
          permissions,
        });
        alert('Employee updated successfully!');
      } else {
        // Create new employee
        await employeesApi.create({
          first_name: firstName,
          last_name: lastName,
          pin,
          employee_number: employeeNumber || undefined,
          role,
          permissions,
        });
        alert('Employee created successfully!');
      }

      // Reset form
      setFirstName('');
      setLastName('');
      setPin('');
      setEmployeeNumber('');
      setRole('employee');
      setPermissions(DEFAULT_PERMISSIONS);
      setShowAddModal(false);
      setEditingEmployee(null);

      // Reload list
      await loadEmployees();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFirstName(employee.first_name);
    setLastName(employee.last_name);
    setEmployeeNumber(employee.employee_number || '');
    setRole(employee.role);
    setPermissions(employee.permissions || DEFAULT_PERMISSIONS);
    setPin(''); // Don't show existing PIN
    setShowAddModal(true);
  };

  const handleDelete = async (employee: Employee) => {
    if (!window.confirm(`Delete employee ${employee.first_name} ${employee.last_name}?`)) {
      return;
    }

    try {
      await employeesApi.delete(employee.id);
      alert('Employee deleted successfully');
      await loadEmployees();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete employee');
    }
  };

  const handleToggleStatus = async (employee: Employee) => {
    const newStatus = employee.status === 'active' ? 'inactive' : 'active';

    try {
      await employeesApi.update(employee.id, { status: newStatus });
      await loadEmployees();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update employee status');
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Employees</h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage warehouse employees and their PIN access
          </p>
        </div>
        <button
          onClick={() => {
            setEditingEmployee(null);
            setFirstName('');
            setLastName('');
            setPin('');
            setEmployeeNumber('');
            setRole('employee');
            setPermissions(DEFAULT_PERMISSIONS);
            setShowAddModal(true);
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
        >
          Add Employee
        </button>
      </div>

      {/* Employees Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <div className="overflow-auto flex-1">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Employee #
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-600">
                  Loading employees...
                </td>
              </tr>
            )}
            {!loading && employees.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-600">
                  No employees yet. Add your first employee to get started.
                </td>
              </tr>
            )}
            {!loading && employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-slate-900">
                    {employee.first_name} {employee.last_name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-slate-600">
                    {employee.employee_number || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                    {employee.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleStatus(employee)}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      employee.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {employee.status}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {new Date(employee.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                  <button
                    onClick={() => handleEdit(employee)}
                    className="text-brand-blue hover:text-brand-blue-hover font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(employee)}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Add/Edit Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowAddModal(false)}
            />
            <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
              <div className="px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900">
                  {editingEmployee ? 'Edit Employee' : 'Add Employee'}
                </h2>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    PIN (4-6 digits) *
                  </label>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    pattern="\d{4,6}"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder={editingEmployee ? 'Leave blank to keep current PIN' : ''}
                    required={!editingEmployee}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Employee Number
                  </label>
                  <input
                    type="text"
                    value={employeeNumber}
                    onChange={(e) => setEmployeeNumber(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Role
                  </label>
                  <select
                    value={role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      setRole(newRole);
                      // Auto-set all permissions for admin
                      if (newRole === 'admin') {
                        setPermissions(AVAILABLE_PERMISSIONS.map(p => p.key));
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  >
                    <option value="employee">Employee</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin (All Access)</option>
                  </select>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Permissions
                  </label>
                  {role === 'admin' ? (
                    <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                      Admin has access to all features
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-lg max-h-48 overflow-y-auto">
                      {AVAILABLE_PERMISSIONS.map(perm => (
                        <label key={perm.key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permissions.includes(perm.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPermissions([...permissions, perm.key]);
                              } else {
                                setPermissions(permissions.filter(p => p !== perm.key));
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                          />
                          <span className="text-slate-700">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setPermissions(AVAILABLE_PERMISSIONS.map(p => p.key))}
                      className="text-xs text-brand-blue hover:underline"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setPermissions(DEFAULT_PERMISSIONS)}
                      className="text-xs text-brand-blue hover:underline"
                    >
                      Reset to Default
                    </button>
                    <button
                      type="button"
                      onClick={() => setPermissions([])}
                      className="text-xs text-slate-500 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingEmployee ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
